/**
 * iOS standalone PWA "orphan strip" workaround (WebKit bug 313800).
 *
 * On some iOS 26.x installs (viewport-fit=cover + the black-translucent status
 * bar) the layout viewport of the Home-Screen app is shorter than the screen:
 * window.innerHeight = screen.height - 62 while the safe-area insets are still
 * reported, leaving a strip below the layout viewport that only the html canvas
 * colour is guaranteed to paint. The geometry is baked at install time
 * (WebKit 317153), so it can appear after a re-install.
 *
 * This measures that gap and exposes it on <html>:
 *   --vp-gap-bottom: <gap>px, --vp-screen-h: <portrait screen height>px and the
 *   `vp-gap` class, all only while a gap is detected.
 * Desktop, Android and a normal Safari tab always measure 0, so nothing changes
 * there. index.html runs an inline copy of measureViewportGap() before the CSS
 * loads (keep both in sync); this module takes over once the app boots.
 */

/** A larger gap is not this bug (browser UI, split view...): ignore it. */
export const VP_GAP_MAX = 120;
export const VP_GAP_CLASS = "vp-gap";
export const VP_GAP_VAR = "--vp-gap-bottom";
export const VP_SCREEN_VAR = "--vp-screen-h";

export type ViewportSnapshot = {
  innerWidth: number;
  innerHeight: number;
  screenWidth: number;
  screenHeight: number;
  /** navigator.standalone (iOS only). */
  standalone?: boolean;
  /** matchMedia("(display-mode: standalone)").matches */
  displayModeStandalone: boolean;
  /** CSS.supports("-webkit-touch-callout", "none"): WebKit on iOS. */
  touchCallout: boolean;
  maxTouchPoints: number;
  visualViewport?: { height: number; scale: number } | null;
  /** A text field has focus (the keyboard may be up). */
  editableFocused: boolean;
};

export type ViewportGap = { gap: number; screenHeight: number };

/**
 * The gap below the layout viewport, 0 when there is none, or null when the
 * keyboard or a pinch-zoom makes the reading unreliable (keep the last value).
 */
export function measureViewportGap(s: ViewportSnapshot): ViewportGap | null {
  const none = { gap: 0, screenHeight: 0 };
  // iOS only: Android standalone also has innerHeight < screen.height (system
  // bars), and shifting there would push the tab bar off screen.
  const iosStandalone =
    s.standalone === true || (s.displayModeStandalone && s.touchCallout);
  if (!iosStandalone || s.maxTouchPoints <= 0) return none;
  if (s.innerHeight <= s.innerWidth) return none; // landscape
  const vv = s.visualViewport;
  if (
    s.editableFocused ||
    (vv &&
      (Math.abs(vv.height - s.innerHeight) > 1 || Math.abs(vv.scale - 1) > 0.01))
  ) {
    return null;
  }
  const screenHeight = Math.max(s.screenWidth, s.screenHeight);
  const gap = Math.max(0, Math.round(screenHeight - s.innerHeight));
  if (gap <= 0 || gap > VP_GAP_MAX) return none;
  return { gap, screenHeight };
}

const NON_TEXT_INPUTS = /^(button|checkbox|color|file|hidden|image|radio|range|reset|submit)$/;

const isEditable = (element: Element | null) =>
  element instanceof HTMLElement &&
  (element.isContentEditable ||
    (element instanceof HTMLTextAreaElement && !element.readOnly) ||
    (element instanceof HTMLInputElement &&
      !element.readOnly &&
      !NON_TEXT_INPUTS.test(element.type)));

export function snapshotWindow(win: Window = window): ViewportSnapshot {
  const nav = win.navigator as Navigator & { standalone?: boolean };
  const css = (win as Window & { CSS?: typeof CSS }).CSS;
  return {
    innerWidth: win.innerWidth,
    innerHeight: win.innerHeight,
    screenWidth: win.screen.width,
    screenHeight: win.screen.height,
    standalone: nav.standalone,
    displayModeStandalone:
      win.matchMedia?.("(display-mode: standalone)").matches ?? false,
    touchCallout:
      typeof css?.supports === "function" &&
      css.supports("-webkit-touch-callout", "none"),
    maxTouchPoints: nav.maxTouchPoints ?? 0,
    visualViewport: win.visualViewport,
    editableFocused: isEditable(win.document.activeElement),
  };
}

/** Write (or clear) the gap on <html>. */
export function applyViewportGap(root: HTMLElement, { gap, screenHeight }: ViewportGap) {
  if (gap > 0) {
    root.style.setProperty(VP_GAP_VAR, `${gap}px`);
    root.style.setProperty(VP_SCREEN_VAR, `${screenHeight}px`);
    root.classList.add(VP_GAP_CLASS);
  } else {
    root.style.removeProperty(VP_GAP_VAR);
    root.style.removeProperty(VP_SCREEN_VAR);
    root.classList.remove(VP_GAP_CLASS);
  }
}

/** Measure now and on every viewport change; returns the cleanup. */
export function watchViewportGap(win: Window = window): () => void {
  const root = win.document.documentElement;
  const update = () => {
    const next = measureViewportGap(snapshotWindow(win));
    if (next) applyViewportGap(root, next);
  };
  // iOS settles innerHeight a frame after orientationchange.
  const updateSoon = () => win.setTimeout(update, 50);
  update();
  win.addEventListener("resize", update);
  win.addEventListener("pageshow", update);
  win.addEventListener("orientationchange", updateSoon);
  win.document.addEventListener("focusout", updateSoon);
  win.visualViewport?.addEventListener("resize", update);
  return () => {
    win.removeEventListener("resize", update);
    win.removeEventListener("pageshow", update);
    win.removeEventListener("orientationchange", updateSoon);
    win.document.removeEventListener("focusout", updateSoon);
    win.visualViewport?.removeEventListener("resize", update);
  };
}
